import { Component, ViewChild } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { finalize } from 'rxjs/operators';
import { forkJoin as ObservableForkJoin } from 'rxjs/observable/forkJoin';
import { of as ObservableOf } from 'rxjs/observable/of';
import { GridOptions } from 'ag-grid';
import {
  defer as _defer,
  find as _find,
  filter as _filter,
  map as _map,
  isEmpty as _isEmpty,
  isNil as _isNil,
  padStart as _padStart,
  padEnd as _padEnd,
  get as _get,
} from 'lodash';

import { StringUtils } from '@blcloud/bl-common';
import {
  ICustomerInfo,
  IDmBizNegoTarget,
  IItemServiceSetting,
  IVehicleInfo,
  IWebReserve,
  IWebReserveLinkReq,
  IWebReserveLinkCandidate,
  ISnsPotentialCustomer,
  ISnsShopAccountSetting
} from '@blcloud/bl-datamodel';
import { WebReserveStatus } from '@blcloud/bl-datamodel/enum/webreserve/web-reserve-status';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';
import { PrivCorpDiv } from '@blcloud/bl-datamodel/enum/customer/priv-corp-div';
import {
  AbstractBlModal,
  BlDialogService,
  BlReferenceGridComponent,
  BlModalService,
  ModalReason,
  ModalOption,
  ModalSize,
} from '@blcloud/bl-ng-ui-component';
import { PageId } from '@blcloud/bl-ng-share-module';
import { ISnsLinkChangeConfirmModalData, SnsLinkChangeConfirmModalComponent,
 SnsLinkChangeConfirmModalDisplayMode } from '@blcloud/bl-ng-vehicle-customer-module';

import { SnsDefinitionMap, SnsFollowerCalledDefault } from '../../core/const/sns-define';
import { WebReserveReceptionLinkComponent } from '../web-reserve-reception-link';
import { ColumnDefs, SELECT_INIT_SIZE, SELECT_MAX_SIZE } from './web-reserve-customer-vehicle-link.define';
import { WebReserveCustomerVehicleLinkService } from './web-reserve-customer-vehicle-link.service';

 /**
 * Web予約顧客車両紐付モーダル
 *
 */
@Component({
  selector: 'app-web-reserve-customer-vehicle-link',
  templateUrl: './web-reserve-customer-vehicle-link.component.html',
  styleUrls: ['./web-reserve-customer-vehicle-link.component.scss']
})
export class WebReserveCustomerVehicleLinkComponent extends AbstractBlModal {

  /** 参照用グリッド */
  @ViewChild('refGrid') refGrid: BlReferenceGridComponent;

  /** OKボタン非活性 */
  _disableOkButton = true;

  /** お客様名 */
  _customerName: string;

  /** フリガナ */
  _customerKanaName: string;

  /** 連絡先(電話) */
  _contactTelInformation: string;

  /** 連絡先(メールアドレス) */
  _contactEmailInformation: string;

  /** 車両登録番号 */
  _dispNumberPlate: string;

  /** メーカー */
  _vehicleName: string;

  /** 車種 */
  _vehicleModelFullWName: string;

  /** グリッドオプション */
  _gridOptions: GridOptions;

  /** グリッドページあたりの件数 */
  _gridSizePatterns: number[] = [];

  /** 詳細グリッドヘッダの高さ */
  _gridHeaderHeight = 37;

  /** 詳細グリッドの高さ */
  _gridHeight = '100%';

  /** 詳細グリッド行の高さ */
  _gridRowHeight = 35;

  /** 検索結果件数 */
  _totalCount = 0;

  /** ヘッダーボタンの表示フラグ */
  _isHeaderBtn = false;

  /** ボタンテキスト */
  _headerBtnText = '＋　新規で顧客・車両を作成';

  /** 最大表示フラグ */
  _isSelectMax = false;

  /** 最大表示ボタンフラグ */
  _isSelectMaxBtn = false;

  /** @ignore 表示件数超過メッセージ */
  _tooManyResultsMessage: string;

  /** 検索数(最大表示500件) */
  _selectMaxSize = SELECT_MAX_SIZE;

  /** BLテナントID */
  private blTenantId: string;

  /** Web予約番号 */
  private webReserveNum: string;

  /** プロダクトコード */
  private productCode: string;

  /** DMのリンクからの予約 */
  private isDm: boolean;

  /** 選択データ */
  private selectedData = null;

  /** Web予約情報 */
  private webReserve: IWebReserve;

  /** Web予約情報紐付候補 */
  private webReserveLinkCandidates: IWebReserveLinkCandidate[];

  /** Web予約確定メッセージ */
  private readonly DONE_MESSAGE = 'Web予約を確定しました。';

  /** @ignore 画面ID */
  private pageId = PageId.WebReserveCustomerVehicleLinkModal;

  /**
   * コンストラクタ.
   */
  constructor(
    private service: WebReserveCustomerVehicleLinkService,
    private modalService: BlModalService,
    private dialogService: BlDialogService,
  ) {
    super();
    this.initGrid();
  }

  /** @override */
  defaultOption(): ModalOption {
    return { animated: true, size: ModalSize.Full };
  }

  /**
   * @override
   */
  getResults(): any {
    return {};
  }

  /**
   * 表示後初期化処理.
   */
  onShown() {
    this.showLoading();
    this.afterShow();
  }

  /**
   * 行選択時のイベント動作
   */
  onSelectedRow(e: any) {
    this.selectedData = e.data;
  }

  /**
   * @override
   */
  afterShow() {
    this.blTenantId = this.data.blTenantId;
    this.webReserveNum = this.data.webReserveNum;
    // ローディング開始
    this.showLoading();
    // Web予約情報を取得
    this.service.getWebReserve(this.blTenantId, this.webReserveNum).subscribe(webReserve => {
      this.webReserve = webReserve;
      // DMのリンクからの予約であるかチェック
      this.checkDm(webReserve);
      // Web予約内容を設定
      this.setWebReserveInfo(webReserve);
      // Web予約ステータスが「確認済」の場合
      if (webReserve.webReserveStatus === WebReserveStatus.Confirmed) {
        // ヘッダーボタン非表示
        this._isHeaderBtn = false;
        // 参照用グリッドのメッセージ設定
        const message = webReserve.logicalDeleteDiv === LogicalDeleteDiv.Valid ?
          '既に顧客・車両情報の紐付が完了しています。' : '既に顧客・車両情報の紐付が削除済みです。';
        this.refGrid.setNoRowsMessage(message);
        this.refGrid.showNoRows();
        // ローディング終了
        this.hideLoading();
        // 処理終了
        return;
      }
      // 通常の予約
      if (!this.isDm) {
        ObservableForkJoin(
          // 初期表示件数を指定して紐付ける顧客・車両情報の候補を取得
          this.service.getCustomerVehicle(this.webReserveNum, SELECT_INIT_SIZE),
          // 商品サービス設定を取得
          this.service.getItemServiceSetting(this.blTenantId, webReserve.itemServiceNum),
        ).pipe(
          finalize(() => {
            // ローディング終了
            this.hideLoading();
          })
        ).subscribe(
          response => {
            this.webReserveLinkCandidates = response[0];
            const itemServiceSetting: IItemServiceSetting = response[1];
            if (itemServiceSetting) {
              this.productCode = itemServiceSetting.productCode;
            }
            // 紐付ける顧客・車両情報の候補を設定
            this.setRowData(this.webReserveLinkCandidates, SELECT_INIT_SIZE);
          }
        );
      // DMリンクからの予約
      } else {
        ObservableForkJoin(
          // 顧客情報を取得
          this.service.getCustomerInfo(webReserve.webReserveBlTenantId, webReserve.customerCode),
          // 車両情報を取得
          this.service.getVehicleInfo(webReserve.webReserveBlTenantId, webReserve.vehicleMgtNumber),
          // 商品サービス設定を取得
          this.service.getItemServiceSetting(webReserve.webReserveBlTenantId, webReserve.itemServiceNum),
        ).pipe(
          finalize(() => {
            // ローディング終了
            this.hideLoading();
          })
        ).subscribe(
          response => {
            const customerInfo: ICustomerInfo = response[0];
            const vehicleInfo: IVehicleInfo = response[1];
            const itemServiceSetting: IItemServiceSetting = response[2];
            if (itemServiceSetting) {
              this.productCode = itemServiceSetting.productCode;
            }
            if (customerInfo.logicalDeleteDiv === LogicalDeleteDiv.Deleted &&
              vehicleInfo.logicalDeleteDiv === LogicalDeleteDiv.Deleted) {
              // 顧客・車両情報を復活
              this.showRestorationConfirmModal(customerInfo, vehicleInfo);
            } else if (customerInfo.logicalDeleteDiv !== LogicalDeleteDiv.Deleted &&
              vehicleInfo.logicalDeleteDiv === LogicalDeleteDiv.Deleted) {
              // 車両情報のみを復活
              this.showRestorationConfirmModal(customerInfo, vehicleInfo, true);
            } else {
              // 紐付ける顧客・車両情報の候補を設定
              this.setRowDataByDm(customerInfo, vehicleInfo);
            }
          }
        );
      }
    });
  }

  /**
   * @override
   */
  dismiss(reason: ModalReason) {
    if (ModalReason.Done === reason) {
      this.onDone();
    } else {
      this.emitDismissEvent(reason);
    }
  }

  /**
   * 新規作成ボタン押下のイベント動作
   */
  onClickAdd(): void {
    // 決定ボタン押下時処理
    this.onDone(true);
 }

  /**
   * 決定ボタン押下時処理
   * @param isClickAdd 新規作成ボタンを押下したかどうか
   */
  onDone(isClickAdd: boolean = false) {
    // 処理中にOKボタンが押されないようにする
    this._disableOkButton = true;

    // ローディング開始
    this.showLoading();
    this.service.getSnsPotentialCustomerFromWebReserve$(this.webReserve).subscribe(
      wrSnsPotentialCustomer => {
        if (!_isNil(wrSnsPotentialCustomer)) {
          // SNSからのweb予約の場合(対応するサービスにも加入中である)
          if (wrSnsPotentialCustomer.customerCode !== 0 && !isClickAdd && this.selectedData.customerCode !== 0 &&
              wrSnsPotentialCustomer.customerCode === this.selectedData.customerCode) {
            // 新規作成でなく、選択顧客とweb予約見込客の紐付き顧客が一致する場合

            // そのままスルーしてweb予約の顧客車両紐付け処理へ
            this.startToLinkCustomerWithWebReserve(isClickAdd);
          } else if (wrSnsPotentialCustomer.customerCode !== 0) {
            // web予約見込客が別の顧客と紐付き済の場合

            // web予約に紐付済みの顧客情報を取得
            this.service.getCustomerInfo(wrSnsPotentialCustomer.snsBlTenantId, wrSnsPotentialCustomer.customerCode).subscribe(
              customerInfo => {
                // ローディング終了
                this.hideLoading();

                // 紐付け解除確認モーダルを表示する
                const unlinkData: ISnsLinkChangeConfirmModalData = {
                  displayMode: SnsLinkChangeConfirmModalDisplayMode.UnlinkForWebReserve,
                  snsPotentialCustomer: wrSnsPotentialCustomer,
                  customerInfo,
                };
                this.modalService.show(SnsLinkChangeConfirmModalComponent, { data: unlinkData }).subscribe(
                  modalRef => {
                    modalRef.hide();

                    if (modalRef.reason === ModalReason.Done) {
                      // OKならweb予約のSNS見込客の顧客紐付けを解除する

                      // ローディング開始
                      this.showLoading();

                      // SNS店舗アカウント設定を取得
                      this.service.getSnsShopAccountSetting$(wrSnsPotentialCustomer.snsBlTenantId,
                                                             wrSnsPotentialCustomer.snsDiv,
                                                             wrSnsPotentialCustomer.snsLinkageId).subscribe(
                        shopAccount => {
                          // 紐付け解除処理
                          this.service.unlinkSnsPotentialCustomer$(wrSnsPotentialCustomer, customerInfo, shopAccount,
                                                                   this.pageId).subscribe(
                            result => {
                              // 選択顧客が別のSNS見込客と紐付いていないかチェックに進む
                              this.checkSelectedCustomerIsAlreadyLinkedAnotherAccount(isClickAdd, result, shopAccount);
                            }
                          );
                        }
                      );
                    } else {
                      // 解除キャンセルの場合
                      this._disableOkButton = false;
                    }
                  }
                );
              }
            );
          } else {
            // web予約見込客が別の顧客とは紐づいていない場合

            // 選択顧客が別のSNS見込客と紐付いていないかのチェックに進む
            this.checkSelectedCustomerIsAlreadyLinkedAnotherAccount(isClickAdd, wrSnsPotentialCustomer, null);
          }
        } else {
          // それ以外のweb予約の場合(またはサービス加入中でない)
          this.startToLinkCustomerWithWebReserve(isClickAdd);
        }
      }
    );
  }

  /**
   * @ignore
   * 顧客が別見込客と紐付き済みかチェック
   *
   * @param createNewCustomer 新規顧客作成フラグ
   * @param snsPotentialCustmer web予約紐づきSNS見込客
   * @param snsShopAccountSetting SNS店舗アカウント設定
   */
  private checkSelectedCustomerIsAlreadyLinkedAnotherAccount(createNewCustomer: boolean, snsPotentialCustmer: ISnsPotentialCustomer,
                                                             snsShopAccountSetting: ISnsShopAccountSetting) {
    if (createNewCustomer) {
      // 新規作成の場合、顧客はこの時点で存在せず別見込客と紐づく可能性はないので、web予約の顧客紐付け処理へ進む

      // web予約の顧客紐付け処理へ
      this.startToLinkCustomerWithWebReserve(createNewCustomer);
      return;
    }

    // 紐付け先の顧客情報を取得する
    const tenantId = this.webReserve.webReserveBlTenantId;
    this.service.getCustomerInfo(tenantId, this.selectedData.customerCode).subscribe(
      customerInfo => {
        const linkedAccount = _find(customerInfo.customerSnsInfoList, snsInfo => {
          return snsInfo.snsDiv === snsPotentialCustmer.snsDiv && snsInfo.snsLinkageId === snsPotentialCustmer.snsLinkageId;
        });

        if (!_isNil(linkedAccount)) {
          if (linkedAccount.snsId === snsPotentialCustmer.snsId) {
            // 選択顧客に紐づくSNS見込客がweb予約見込客の場合
            // ※このパターンは処理の流れ上ないと思われる

            // ローディング終了
            this.hideLoading();

            // web予約の顧客車両紐付け処理へ
            this.startToLinkCustomerWithWebReserve(createNewCustomer);
          } else {
            // 選択顧客に別のSNS見込客が紐づき済みの場合
            // 別アカウントとの紐付け解除を確認
            this.service.getSnsPotentialCustomer$(tenantId, linkedAccount.snsLinkageId, linkedAccount.snsId).subscribe(
              linkedSnsPotentialCustomer => {
                // ローディング終了
                this.hideLoading();

                const unlinkData: ISnsLinkChangeConfirmModalData = {
                  displayMode: SnsLinkChangeConfirmModalDisplayMode.UnlinkToLinkAnotherOne,
                  snsPotentialCustomer: linkedSnsPotentialCustomer,
                  customerInfo,
                };
                this.modalService.show(SnsLinkChangeConfirmModalComponent, { data: unlinkData }).subscribe(
                  modalRef => {
                    modalRef.hide();
                    if (modalRef.reason === ModalReason.Done) {
                      // OKなら選択顧客の顧客紐付けを解除する

                      // ローディング開始
                      this.showLoading();

                      let shopAccount$: Observable<ISnsShopAccountSetting>;

                      if (_isNil(snsShopAccountSetting)) {
                        // 店舗アカウントが不明な場合は取得
                        shopAccount$ = this.service.getSnsShopAccountSetting$(linkedSnsPotentialCustomer.snsBlTenantId,
                                                                              linkedSnsPotentialCustomer.snsDiv,
                                                                              linkedSnsPotentialCustomer.snsLinkageId);
                      } else {
                        shopAccount$ = ObservableOf(snsShopAccountSetting);
                      }
                      shopAccount$.subscribe(
                        shopAccount => {
                          // 紐付解除処理
                          this.service.unlinkSnsPotentialCustomer$(linkedSnsPotentialCustomer, customerInfo,
                                                                   shopAccount,
                                                                   this.pageId).subscribe(
                            result => {
                              // ローディング終了
                              this.hideLoading();
                              // 顧客紐付け確認処理へ
                              this.confirmToLinkCustomer(createNewCustomer, result, customerInfo, shopAccount);
                            }
                          );
                        }
                      );
                    } else {
                      // 解除キャンセルの場合
                      this._disableOkButton = false;
                    }
                  }
                );
              }
            );
          }
        } else {
          // 紐づきなしの場合

          // ローディング終了
          this.hideLoading();

          // 顧客紐付け確認処理へ
          this.confirmToLinkCustomer(createNewCustomer, snsPotentialCustmer, customerInfo, snsShopAccountSetting);
        }
      }
    );
  }

  /**
   * @ignore
   * SNS見込客顧客紐付け確認処理
   *
   * @param createNewCustomer 新規顧客作成フラグ
   * @param snsPotentialCustmer web予約紐づきSNS見込客
   * @param customerInfo 選択顧客
   * @param snsShopAccountSetting SNS店舗アカウント設定
   */
  private confirmToLinkCustomer(createNewCustomer: boolean, snsPotentialCustomer: ISnsPotentialCustomer,
                                customerInfo: ICustomerInfo, snsShopAccountSetting: ISnsShopAccountSetting) {
    const unlinkData: ISnsLinkChangeConfirmModalData = {
      displayMode: SnsLinkChangeConfirmModalDisplayMode.LinkForWebReserve,
      snsPotentialCustomer,
      customerInfo,
    };
    this.modalService.show(SnsLinkChangeConfirmModalComponent, { data: unlinkData }).subscribe(
      modalRef => {
        modalRef.hide();
        if (modalRef.reason === ModalReason.Done) {
          // OKなら選択顧客とSNS見込客を紐付ける

          // ローディング開始
          this.showLoading();

          let shopAccount$: Observable<ISnsShopAccountSetting>;

          if (_isNil(snsShopAccountSetting)) {
            // 店舗アカウントが不明な場合は取得
            shopAccount$ = this.service.getSnsShopAccountSetting$(snsPotentialCustomer.snsBlTenantId,
                                                                  snsPotentialCustomer.snsDiv,
                                                                  snsPotentialCustomer.snsLinkageId);
          } else {
            shopAccount$ = ObservableOf(snsShopAccountSetting);
          }
          shopAccount$.subscribe(
            shopAccount => {

              // 顧客が編集中でないかをチェック
              this.service.customerIsEditing$(customerInfo.customerCode).subscribe(
                isEditing => {
                  if (isEditing) {
                    // 編集中の場合

                    const snsDefinitions = _get(SnsDefinitionMap, snsPotentialCustomer.snsDiv);
                    const followerCalled = snsDefinitions ? snsDefinitions.followerCalled : SnsFollowerCalledDefault;
                    // 編集中エラーを表示して終了
                    this.dialogService.warn(`編集中の顧客情報がある為、顧客と${followerCalled}の紐付ができません。\n顧客入力画面で編集中のデータを保存して再度お試しください。`).subscribe(
                      ref => {
                        ref.hide();

                        // ローディング終了
                        this.hideLoading();

                        // OKボタン有効化
                        this._disableOkButton = false;
                      }
                    );
                  } else {
                    // 編集中でない

                    // 紐付け
                    this.service.linkSnsPotentialCustomer$(snsPotentialCustomer, customerInfo, shopAccount, this.pageId).subscribe(
                      () => {
                        // web予約の顧客車両紐付け処理へ
                        this.startToLinkCustomerWithWebReserve(createNewCustomer, true);
                      }
                    );
                  }
                }
              );

            }
          );
        } else {
          // 解除キャンセルの場合
          this._disableOkButton = false;
        }
      }
    );
  }

  /**
   * @ignore
   * Web予約顧客車両紐付け処理の開始
   *
   * @param createNewCustomer 顧客新規作成フラグ
   * @param alreadyConfirmed 紐付け確認済みフラグ
   */
  private startToLinkCustomerWithWebReserve(createNewCustomer: boolean, alreadyConfirmed: boolean = false) {
    // OKボタン有効化
    this._disableOkButton = false;
    // ローディング終了
    this.hideLoading();

    // 車両情報が存在するもののみに絞り込む
    const containingVehicleInfoList = _filter(this.webReserveLinkCandidates, res => {
      return !_isNil(res.vehicleInfo);
    });
    // 紐付け候補の中から選択中の顧客と等しいものを取得
    const targetWebReserveLinkCandidate = _find(containingVehicleInfoList, res =>
      this.selectedData.customerCode === res.customerInfo.customerCode &&
      this.selectedData.vehicleMgtNumber === res.vehicleInfo.vehicleMgtNumber
    );
    if (
      !createNewCustomer &&
      targetWebReserveLinkCandidate
    ) {
      // 受付紐付モーダルを表示
      this.modalService.show(WebReserveReceptionLinkComponent, {
        data: {
          customerCode: targetWebReserveLinkCandidate.customerInfo.customerCode,
          vehicleMgtNumber: targetWebReserveLinkCandidate.vehicleInfo.vehicleMgtNumber,
          conditions: this.service.inProgressReceptionConditions,
          webreserve: this.webReserve,
          productCode: this.productCode
        },
      }).subscribe(
        modalRef => {
          modalRef.hide();
          if (modalRef.reason === ModalReason.Done) {
            this.emitDismissEvent(ModalReason.Done);
          }
        }
      );
    } else if (alreadyConfirmed) {
      // 既に確認済みの場合はダイアログを表示せずに紐付け処理へ進む

      // Web予約情報と顧客・車両情報の候補を紐付け
      this.linkWebReserve(createNewCustomer);
    } else {
      const message = !createNewCustomer ? '顧客・車両情報を紐付けます。よろしいですか？' : '新規で顧客・車両情報を登録します。よろしいですか？';
      this.dialogService.confirm(message).subscribe(
        ref => {
          if (ref.reason === ModalReason.Done) {
            // Web予約情報と顧客・車両情報の候補を紐付け
            this.linkWebReserve(createNewCustomer);
          }
          ref.hide();
          // 参照用グリッドにフォーカスを当てる
          _defer(() => this.refGrid.focus());
        }
      );
    }
  }

  /**
   * グリッドの準備完了時
   */
  onReadyGrid() {
    // リサイズ処理
    this.resizeGrid();
  }

  /**
   * ページング: ページサイズ変更時処理
   * @param size ページサイズ
   */
  onChangePageSize(size: number): void {
    this._gridOptions.paginationPageSize = size;
    this.refGrid.gotoFirstPage();
  }

  /**
   * 最大表示件数を指定して紐付ける顧客・車両情報の候補の検索処理
   */
  onSelectMax(): void {
    // ローディング開始
    this.showLoading();
    // 最大表示件数を指定して紐付ける顧客・車両情報の候補を取得
    this.service.getCustomerVehicle(this.webReserveNum, SELECT_MAX_SIZE).pipe(
      finalize(() => {
        // ローディング終了
        this.hideLoading();
      })
      ).subscribe(webReserveLinkCandidates => {
        // 紐付ける顧客・車両情報の候補を設定
        this.setRowData(webReserveLinkCandidates, SELECT_MAX_SIZE);
      });
  }

  /**
   * グリッド初期化
   */
  private initGrid(): void {
    this._gridOptions = <GridOptions>{};
    this._gridOptions.columnDefs = ColumnDefs;
    this._gridOptions.rowData = [];
    this._gridOptions.paginationPageSize = 50;
    this._gridSizePatterns = [50, 100, 150, 200];
  }

  /**
   * Web予約内容を設定する
   * @param webReserve Web予約情報
   */
  private setWebReserveInfo(webReserve: IWebReserve): void {
    // お客様名
    this._customerName = webReserve.privCorpDiv === PrivCorpDiv.Corporate ?
      StringUtils.concat(webReserve.customerName1, '　', webReserve.customerName2) :
      StringUtils.concat(webReserve.customerFamilyName, '　', webReserve.customerGivenName);

    // フリガナ
    this._customerKanaName = webReserve.privCorpDiv === PrivCorpDiv.Corporate ?
      StringUtils.concat(webReserve.customerNameKana1, '　', webReserve.customerNameKana2) :
      StringUtils.concat(webReserve.customerFamilyNameKana, '　', webReserve.customerGivenNameKana);

    // 電話番号
    this._contactTelInformation =
      webReserve.contactInfoTelList[0].contactInformation ?
      webReserve.contactInfoTelList[0].contactInformation : '';

    // メールアドレス
    this._contactEmailInformation =
      webReserve.contactInfoEmailList[0].contactInformation ?
      webReserve.contactInfoEmailList[0].contactInformation : '';

    // 車両登録番号
    if (Number(webReserve.numPlateSeriesSpecifNum) !== 0) {
      this._dispNumberPlate = StringUtils.concat(
        _padEnd(webReserve.vcleInspRegistOfficeName, 4, '　'),
        ' ',
        _padEnd(webReserve.numPlateClassNum, 3, ' '),
        ' ',
        _padEnd(webReserve.numPlateKana, 1, '　'),
        ' ',
        _padStart(webReserve.numPlateSeriesSpecifNum.toString(), 4, ' '),
      );
    }

    // メーカー
    this._vehicleName = webReserve.blVehicleMakerFullWFixNm;
    // 車種
    this._vehicleModelFullWName = webReserve.vehicleModelFixFullWName ? ' ／ ' + webReserve.vehicleModelFixFullWName : '';

    // 通常の予約
    if (!this.isDm) {
      // ヘッダーボタンを表示する
      this._isHeaderBtn = true;

    // DMのリンクからの予約
    } else {
      // ヘッダーボタンを表示しない
      this._isHeaderBtn = false;
    }
  }

  /**
   * 紐付ける顧客・車両情報の候補の行データを設定
   * @param CustomerVehicleResults Web予約情報紐付候補リスト
   * @param size 検索数
   */
  private setRowData(webReserveLinkCandidates: IWebReserveLinkCandidate[], size: number): void {

    // Web予約情報紐付候補リストから表示用の行データを作成
    const rowData = this.createRowData(webReserveLinkCandidates);

    // 行データを設定
    this._gridOptions.api.setRowData(rowData);

    // 検索結果件数を設定
    this._totalCount = rowData.length;

    // 検索結果件数が０件の場合
    if (this._totalCount === 0) {
      this.refGrid.setNoRowsMessage('該当する候補が見つかりません。');
      this.refGrid.showNoRows();
    // 検索結果件数が１００件以上かつ検索数が初期表示１００件の場合
    } else if (this._totalCount >= SELECT_INIT_SIZE && size === SELECT_INIT_SIZE) {
      // OKボタン活性
      this._disableOkButton = false;
      this._isSelectMax = true;
      this._isSelectMaxBtn = true;
      this._tooManyResultsMessage = '検索結果が100件以上ある為、最有力候補の100件を表示しています。';
    // 検索結果件数が１００件以上かつ検索数が最大表示５００件の場合
    } else if (this._totalCount >= SELECT_INIT_SIZE && size === SELECT_MAX_SIZE) {
      // OKボタン活性
      this._disableOkButton = false;
      this._isSelectMax = true;
      this._isSelectMaxBtn = false;
      this._tooManyResultsMessage = `${this._totalCount}件を表示しています。`;
    } else {
      // OKボタン活性
      this._disableOkButton = false;
    }

    // リサイズ処理
    this.resizeGrid();
  }

  /**
   * Web予約情報紐付候補リストから表示用の行データを作成
   * @param CustomerVehicleResults Web予約情報紐付候補リスト
   */
  private createRowData(webReserveLinkCandidates: IWebReserveLinkCandidate[]): any {
    return _map(webReserveLinkCandidates, data => {
      return {
        // お客様名
        customerName: data.customerInfo ?
          (data.customerInfo.privCorpDiv === PrivCorpDiv.Corporate ?
            StringUtils.concat(data.customerInfo.customerName1, '　', data.customerInfo.customerName2) :
            StringUtils.concat(data.customerInfo.customerFamilyName, '　', data.customerInfo.customerGivenName)
          ) : '',
        // フリガナ
        customerKanaName: data.customerInfo ?
        (data.customerInfo.privCorpDiv === PrivCorpDiv.Corporate ?
          StringUtils.concat(data.customerInfo.customerNameKana1, '　', data.customerInfo.customerNameKana2) :
          StringUtils.concat(data.customerInfo.customerFamilyNameKana, '　', data.customerInfo.customerGivenNameKana)
        ) : '',
        // 連絡先
        contactTelInformation: data.customerInfo && !_isEmpty(data.customerInfo.contactInfoTelList) ?
          data.customerInfo.contactInfoTelList[data.customerInfo.mainContactTelIndex].contactInformation : '',
        // メールアドレス
        contactEmailInformation: data.customerInfo && !_isEmpty(data.customerInfo.contactInfoEmailList) ?
          data.customerInfo.contactInfoEmailList[data.customerInfo.mainContactEmailIndex].contactInformation : '',
        // 車両登録番号
        dispNumberPlate: data.vehicleInfo ?
          {
            vcleInspRegistOfficeName: data.vehicleInfo.vehicleInspCertifInfo.vcleInspRegistOfficeName,
            numPlateClassNum: data.vehicleInfo.vehicleInspCertifInfo.numPlateClassNum,
            numPlateKana: data.vehicleInfo.vehicleInspCertifInfo.numPlateKana,
            numPlateSeriesSpecifNum: data.vehicleInfo.vehicleInspCertifInfo.numPlateSeriesSpecifNum
          } : '新規車両として登録',
        // メーカー／車種　どちらもある場合
        vehicleName: data.vehicleInfo &&
        data.vehicleInfo.vehicleInspCertifInfo.vehicleName &&
        data.vehicleInfo.vehicleSpecificationInfo.vehicleModelFixFullWName ?
          data.vehicleInfo.vehicleInspCertifInfo.vehicleName + ' ／ '
          + data.vehicleInfo.vehicleSpecificationInfo.vehicleModelFixFullWName :
        // メーカーのみの場合
        data.vehicleInfo &&
        data.vehicleInfo.vehicleInspCertifInfo.vehicleName &&
        !data.vehicleInfo.vehicleSpecificationInfo.vehicleModelFixFullWName ?
          data.vehicleInfo.vehicleInspCertifInfo.vehicleName :
        // どちらもない場合
          '',
        // 顧客コード
        customerCode:  data.customerInfo ?
          data.customerInfo.customerCode : null,
        // 車両管理番号
        vehicleMgtNumber: data.vehicleInfo ?
          data.vehicleInfo.vehicleMgtNumber : null,
        };
      });
  }

  /**
   * Web予約情報と顧客・車両情報の候補を紐付ける
   * @param isClickAdd 新規作成ボタンを押下したかどうか
   */
  private linkWebReserve(isClickAdd: boolean = false): void {
    // 紐付け候補一覧から選択中の顧客のものを検索
    const targetWebReserveLinkCandidate = _find(this.webReserveLinkCandidates, res =>
      this.selectedData.customerCode === res.customerInfo.customerCode);
    const webReserveLinkReq: IWebReserveLinkReq = {};

    webReserveLinkReq.blTenantId = this.blTenantId;
    webReserveLinkReq.webReserve = this.webReserve;
    webReserveLinkReq.productCode = this.productCode;
    webReserveLinkReq.customerInfo = !isClickAdd ? targetWebReserveLinkCandidate.customerInfo : null;
    webReserveLinkReq.vehicleInfo = !isClickAdd && this.selectedData.vehicleMgtNumber ? targetWebReserveLinkCandidate.vehicleInfo : null;

    // ローディング開始
    this.showLoading();
    // 通常の予約の場合
    if (!this.isDm) {
      this.service.linkWebReserve(webReserveLinkReq).pipe(
        finalize(() => {
          // ローディング終了
          this.hideLoading();
        })
      ).subscribe(result => {
        if (result !== undefined) {
          this.dialogService.info(this.DONE_MESSAGE)
          .subscribe(blModalRef => {
            blModalRef.hide();
            this.emitDismissEvent(ModalReason.Done);
          });
        }
      });
    // DMリンクからの予約の場合
    } else {
      this.service.linkWebReserveByDm(webReserveLinkReq).pipe(
        finalize(() => {
          // ローディング終了
          this.hideLoading();
        })
      ).subscribe(result => {
        if (result !== undefined) {
          this.dialogService.info(this.DONE_MESSAGE)
          .subscribe(blModalRef => {
            blModalRef.hide();
            this.emitDismissEvent(ModalReason.Done);
          });
        }
      });
    }
  }

  /**
   * リサイズ処理
   */
  private resizeGrid(): void {
    // グリッド枠いっぱいに拡大
    this._gridOptions.api.sizeColumnsToFit();
  }

  /**
   * 顧客・車両情報 復活の確認ダイアログを表示
   *
   * @param customerInfo 顧客情報
   * @param vehicleInfo 車両情報
   * @param isVehicleInfo 車両情報のみ
   */
  private showRestorationConfirmModal(customerInfo: ICustomerInfo, vehicleInfo: IVehicleInfo,
    isVehicleInfo: boolean = false): void {
    const message = isVehicleInfo ? 'DM送信時に存在した車両情報が削除されています。\n車両情報を復活しますか？' :
      'DM送信時に存在した顧客・車両情報が削除されています。\n顧客・車両情報を復活しますか？';
    this.dialogService.confirm(message).subscribe(
      ref => {
        if (ref.reason === ModalReason.Done) {
          // ローディング開始
          this.showLoading();
          if (isVehicleInfo) {
            // 車両情報を復活
            this.service.vehicleRestoration(customerInfo.customerCode, vehicleInfo.vehicleMgtNumber).pipe(
              finalize(() => {
                // ローディング終了
                this.hideLoading();
              })
              ).subscribe(() => {
                // 紐付ける顧客・車両情報の候補を設定
                this.setRowDataByDm(customerInfo, vehicleInfo);
                // 正常ダイアログを表示
                this.showRestorationOkModal();
              }
            );
          } else {
            // 顧客・車両情報を復活
            this.service.restoration(customerInfo.customerCode).pipe(
              finalize(() => {
                // ローディング終了
                this.hideLoading();
              })
              ).subscribe(() => {
                // 紐付ける顧客・車両情報の候補を設定
                this.setRowDataByDm(customerInfo, vehicleInfo);
                // 正常ダイアログを表示
                this.showRestorationOkModal();
              }
            );
          }
        } else {
          this.refGrid.setNoRowsMessage('該当する候補が見つかりません。');
          this.refGrid.showNoRows();
        }
        ref.hide();
        // 参照用グリッドにフォーカスを当てる
        _defer(() => this.refGrid.focus());
      }
    );
  }

  /**
   * 紐付ける顧客・車両情報の候補を設定（DM専用）
   *
   * @param customerInfo 顧客情報
   * @param vehicleInfo 車両情報
   */
  private setRowDataByDm(customerInfo: ICustomerInfo, vehicleInfo: IVehicleInfo): void {
    // 1件のWeb予約情報紐付候補を作成
    const webReserveLinkCandidates: Array<IWebReserveLinkCandidate> = [
      { customerInfo, vehicleInfo }
    ];
    // 紐付ける顧客・車両情報の候補を設定
    this.setRowData(webReserveLinkCandidates, SELECT_INIT_SIZE);
  }

  /**
   * 顧客・車両情報 復活の正常ダイアログを表示
   */
  private showRestorationOkModal(): void {
    const message = '正常に復活しました。\n紐付ける顧客・車両情報の候補をご確認ください。';
    this.dialogService.info(message).subscribe(
      ref => {
        ref.hide();
      }
    );
  }

  /**
   * DMのリンクからの予約であるかチェック
   * @param webReserve Web予約情報
   */
  private checkDm(webReserve: IWebReserve): void {
    const dmBizNegoTarget: IDmBizNegoTarget = webReserve.dmBizNegoTarget;
    // DM商談対象が無い場合
    if (!dmBizNegoTarget) {
      // 通常の予約
      this.isDm = false;
    // DM商談対象に初期値が設定されている場合
    } else if (dmBizNegoTarget.customerCode === 0 &&
      dmBizNegoTarget.vehicleMgtNumber === 0 &&
      dmBizNegoTarget.itemServiceNum === 1 &&
      dmBizNegoTarget.serviceExecuteScheduledDate === '1900-01-01') {
      // 通常の予約
      this.isDm = false;
    } else {
      // DMのリンクからの予約
      this.isDm = true;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/web-reserve-customer-vehicle-link/web-reserve-customer-vehicle-link.component.ts